
import React, { useEffect, useRef, useState } from "react";
import { TabNavigation } from "../../component/navigation/TabNavigation";
import { Breadcrumb } from "../../component/navigation/Breadcrumb";
import { DomainOverview } from "../../component/Layout/Domain/DomainOverview";
import { SubdomainDetail } from "../../component/Layout/Domain/SubdomainDetail";
import axiosInstance from "../../axiosInstanceSetup";
import { ProductPage } from "../../component/Layout/Domain/ProductPage";
import { AssetsSummary } from "../../component/Layout/Domain/AssetsSummary";
import { AssetsDetails } from "../../component/Layout/Domain/AssetsDetails";
import { AssetsDetailsUI } from "../../component/Layout/Domain/AssetsDetailsUI";
import heartLoading from "../../assests/heartLoading.gif";
import { useSearchParams } from "react-router-dom";
import { buildDataDomainUrl } from "../../utils/dataDomainUrl";
import {
  DollarSign,
  ShoppingBag,
  Users,
  Layers,
  Truck,
  FlaskConical,
  MonitorCheck,
  Database
} from 'lucide-react';

const DOMAIN_TYPE_TABS = [
  { id: "all", label: "All" },
  { id: "domains", label: "Domains" },
  { id: "sourceSystems", label: "Source Systems" },
  { id: "analyticalProduct", label: "Analytical Product" },
];

const getDomainTypeTabFromLayers = (layers = []) => {
  if (!Array.isArray(layers) || layers.length === 0) return "all";

  const normalizedLayers = [...new Set(layers.filter(Boolean))].sort();

  if (
    normalizedLayers.length === 1 &&
    normalizedLayers[0] === "analyticalProduct"
  ) {
    return "analyticalProduct";
  }

  if (
    normalizedLayers.every((layer) => ["ingestion", "bronze"].includes(layer))
  ) {
    return "sourceSystems";
  }

  if (
    normalizedLayers.every((layer) => ["silver", "gold"].includes(layer))
  ) {
    return "domains";
  }

  return "all";
};

const getLayersForDomainTypeTab = (tabId) => {
  switch (tabId) {
    case "domains":
      return ["silver", "gold"];
    case "sourceSystems":
      return ["ingestion", "bronze"];
    case "analyticalProduct":
      return ["analyticalProduct"];
    default:
      return [];
  }
};

const getUrlDataLayers = (params) =>
  params
    .getAll("data_layer")
    .flatMap((value) => String(value).split(","))
    .map((value) => value.trim())
    .filter(Boolean);

const getL5ViewType = (type) => (type === "kpi" ? "kpi" : "asset");

function LandingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const lastParentFetchKeyRef = useRef("");

  const [navigation, setNavigation] = useState({});
  const [parentDomainData, setparentDomainData] = useState();
  const [filteredParentDomainData, setFilteredParentDomainData] = useState();
  const [domainData, setDomainData] = useState();
  const [subdomainData, setSubdomainData] = useState();
  const [productData, setProductData] = useState();
  const [loading, setLoading] = useState(false);
  const [domainLoading, setDomainLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
const [reportLoading, setReportLoading] = useState(false);

  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [activeDomainTypeTab, setActiveDomainTypeTab] = useState("all");
  // Filter states - persisted at parent level
  const [appliedFilters, setAppliedFilters] = useState({
    regions: [],
    dataLayer: [],
  });

  // Regions from API - shared across all levels
  const [regions, setRegions] = useState([]);
  const role = localStorage.getItem("role") || "";  

  const fetchparentDomainData = async (
    regions = [],
    dataLayers = [],
    selectedL1 = "",
    updateParentTabs = false,
  ) => {
    const requestKey = JSON.stringify({
      selectedL1,
      regions: [...(regions || [])].filter(Boolean).sort(),
      dataLayers: [...(dataLayers || [])].filter(Boolean).sort(),
    });

    if (lastParentFetchKeyRef.current === requestKey) {
      return;
    }

    lastParentFetchKeyRef.current = requestKey;

    const shouldShowLoader = updateParentTabs;
    const shouldShowDomainLoader = !updateParentTabs;

    try {
      if (shouldShowLoader) {
        setLoading(true);
      }
      if (shouldShowDomainLoader) {
        setDomainLoading(true);
      }

      const params = new URLSearchParams();
      if (selectedL1) params.append("l1", selectedL1);
      regions.forEach((region) => params.append("region", region));
      if (Array.isArray(dataLayers)) {
        dataLayers.forEach((layer) => {
          if (layer && layer.trim()) params.append("data_layer", layer);
        });
      }

      const queryString = params.toString();
      const url = queryString
        ? `${process.env.REACT_APP_API_URL}/domain-entity?${queryString}`
        : `${process.env.REACT_APP_API_URL}/domain-entity`;

      const response = await axiosInstance.get(url);

      if (response.status === 200) {
        const fetchedParentDomainData = response?.data?.data;
        setFilteredParentDomainData(fetchedParentDomainData);

        if (updateParentTabs) {
          setparentDomainData(fetchedParentDomainData);
        }

        const l1 = searchParams.get("l1");
        const l2 = searchParams.get("l2");
        const l3 = searchParams.get("l3");
        const l4 = searchParams.get("l4");
        const l5 = searchParams.get("l5");

        if (l1 && l2 && l3 && l4 && l5) {
          setNavigation({
            level: "L5",
            selectedL1: l1,
            selectedL2: l2,
            selectedL3: l3,
            selectedL4: l4,
            selectedL5: l5,
            selectedType: getL5ViewType(searchParams.get("type")),
          });
          await getProductDetails(l4);
        } else if (l1 && l2 && l3 && l4) {
          setNavigation({ level: "L4", selectedL1: l1, selectedL2: l2, selectedL3: l3, selectedL4: l4 });
          await getProductDetails(l4);
        } else if (l1 && l2 && l3) {
          setNavigation({ level: "L3", selectedL1: l1, selectedL2: l2, selectedL3: l3 });
        } else if (l1 && l2) {
          setNavigation({ level: "L2", selectedL1: l1, selectedL2: l2 });
        } else if (l1) {
          setNavigation({ level: "L1", selectedL1: l1 });
        } else {
          const firstParent =
            fetchedParentDomainData &&
            fetchedParentDomainData[0] &&
            fetchedParentDomainData[0].parent_L1;
          const newNavigation = {
            level: "L1",
            selectedL1: `${firstParent}`,
          };
          setNavigation(newNavigation);
          updateURLParams(newNavigation, dataLayers);
        }
      }
    } catch (error) {
      lastParentFetchKeyRef.current = "";
      console.error("Error fetching domain information", error);
    } finally {
      if (shouldShowLoader) {
        setLoading(false);
      }
      if (shouldShowDomainLoader) {
        setDomainLoading(false);
      }
    }
  };

  const fetchDomainData = async (l1, l2, domainId = "", regions = [], dataLayers = []) => {
    const params = new URLSearchParams();
    if (l1) params.append("l1", l1);
    if (l2) params.append("l2", l2);
    if (domainId) params.append("id", domainId);
    regions.forEach((region) => params.append("region", region));
    if (Array.isArray(dataLayers)) {
      dataLayers.forEach((layer) => {
        if (layer && layer.trim()) params.append("data_layer", layer);
      });
    }
    const queryString = params.toString();
    const url = `${process.env.REACT_APP_API_URL}/domain-entity${queryString ? `?${queryString}` : ""}`;
    const response = await axiosInstance.get(url);
    if (response.status === 200) {
      const data = response?.data?.data;
      setDomainData(data?.[0]);
      return data?.[0];
    }
    return null;
  };

  const fetchSubDomainData = async (l1, l2, l3, subDomainId = "", regions = [], dataLayers = []) => {
    const params = new URLSearchParams();
    if (l1) params.append("l1", l1);
    if (l2) params.append("l2", l2);
    if (l3) params.append("l3", l3);
    if (subDomainId) params.append("id", subDomainId);
    regions.forEach((region) => params.append("region", region));
    if (Array.isArray(dataLayers)) {
      dataLayers.forEach((layer) => {
        if (layer && layer.trim()) params.append("data_layer", layer);
      });
    }
    const queryString = params.toString();
    const url = `${process.env.REACT_APP_API_URL}/domain-entity${queryString ? `?${queryString}` : ""}`;
    const response = await axiosInstance.get(url);
    if (response.status === 200) {
      const data = response?.data?.data;
      setSubdomainData(data?.[0]);
      return data?.[0];
    }
    return null;
  };

  const fetchProductData = async (l1, l2, l3, l4, productId = "", regions = [], dataLayers = []) => {
    const params = new URLSearchParams();
    if (l1) params.append("l1", l1);
    if (l2) params.append("l2", l2);
    if (l3) params.append("l3", l3);
    if (l4) params.append("l4", l4);
    if (productId) params.append("id", productId);
    regions.forEach((region) => params.append("region", region));
    if (Array.isArray(dataLayers)) {
      dataLayers.forEach((layer) => {
        if (layer && layer.trim()) params.append("data_layer", layer);
      });
    }
    const queryString = params.toString();
    const url = `${process.env.REACT_APP_API_URL}/domain-entity${queryString ? `?${queryString}` : ""}`;
    const response = await axiosInstance.get(url);
    if (response.status === 200) {
      const data = response?.data;
      setProductData(data);
      return data;
    }
    return null;
  };

  const fetchKpiData = async (l1, l2, l3, l4, l5) => {
    setReportLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("l1", l1);
      params.append("l2", l2);
      params.append("l3", l3);
      params.append("l4", l4);
      params.append("l5", l5);
      params.append("type", "kpi");
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/domain-entity?${params.toString()}`,
      );
      if (response.status === 200) {
        setReportData(response.data);
      }
    } catch (e) {
      setReportData([]);
      console.error("Error fetching KPI data:", e);
    } finally {
      setReportLoading(false);
    }
  };

  // Helper function to update URL parameters
const updateURLParams = (newNavigation, dataLayers = appliedFilters.dataLayer || [], type = "") => {
  const params = new URLSearchParams();
  if (newNavigation.selectedL1) params.set("l1", newNavigation.selectedL1);
  if (newNavigation.selectedL2) params.set("l2", newNavigation.selectedL2);
  if (newNavigation.selectedL3) params.set("l3", newNavigation.selectedL3);
  if (newNavigation.selectedL4) params.set("l4", newNavigation.selectedL4);
  if (newNavigation.selectedL5) params.set("l5", newNavigation.selectedL5);
  const resolvedType = type || newNavigation.selectedType;
  if (resolvedType === "kpi") params.set("type", resolvedType);
 
  if (Array.isArray(dataLayers)) {
    dataLayers.forEach((layer) => {
      if (layer && String(layer).trim()) {
        params.append("data_layer", String(layer).trim());
      }
    });
  }
 
  setSearchParams(params);
};

  const getProductDetails = async (productName) => {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_API_URL}/get-product-submission/${productName}`
    );

    if (response.status === 200 || response.status === 201) {
      setSelectedProductDetails(response.data);
      return response.data;
    }
    return null;
  };

  // Handle region and data-layer filter changes and keep tabs in sync.
  const handleFilterChange = (
    regions,
    dataLayers = appliedFilters.dataLayer || [],
  ) => {
    const normalizedDataLayers = Array.isArray(dataLayers)
      ? dataLayers.filter(Boolean)
      : [];
    const matchedTab = getDomainTypeTabFromLayers(normalizedDataLayers);

    setAppliedFilters({ regions, dataLayer: normalizedDataLayers });
    setActiveDomainTypeTab(matchedTab);

    const {
      level,
      selectedL1,
      selectedL2,
      selectedL3,
      selectedL4,
      selectedL5,
      selectedType,
    } = navigation;
    const effectiveL1 = selectedL1 || searchParams.get("l1") || "";

    const syncedNavigation = {
      level: level || "L1",
      selectedL1: effectiveL1 || undefined,
      selectedL2,
      selectedL3,
      selectedL4,
      selectedL5,
      selectedType,
    };

    updateURLParams(syncedNavigation, normalizedDataLayers);

    if (level === "L1" || !level) {
      fetchparentDomainData(regions, normalizedDataLayers, effectiveL1);
    } else if (level === "L2") {
      fetchDomainData(
        effectiveL1,
        selectedL2,
        "",
        regions,
        normalizedDataLayers,
      );
    } else if (level === "L3") {
      fetchSubDomainData(
        effectiveL1,
        selectedL2,
        selectedL3,
        "",
        regions,
        normalizedDataLayers,
      );
    } else if (level === "L4" || level === "L5") {
      fetchProductData(
        effectiveL1,
        selectedL2,
        selectedL3,
        selectedL4,
        "",
        regions,
        normalizedDataLayers,
      );
    }
  };

  const handleDomainTypeTabChange = async (tabId, selectedL1FromDomain) => {
    const presetLayers = getLayersForDomainTypeTab(tabId);
    const selectedL1 =
      selectedL1FromDomain ||
      navigation.selectedL1 ||
      searchParams.get("l1") ||
      parentDomainData?.[0]?.parent_L1 ||
      "";

    const newNavigation = {
      level: "L1",
      selectedL1,
      selectedL2: undefined,
      selectedL3: undefined,
      selectedL4: undefined,
      selectedL5: undefined,
      selectedType: undefined,
    };

    setNavigation(newNavigation);
    updateURLParams(newNavigation, presetLayers);
    setActiveDomainTypeTab(tabId);
    setAppliedFilters((prev) => ({
      ...prev,
      dataLayer: presetLayers,
    }));

    await fetchparentDomainData(
      appliedFilters.regions || [],
      presetLayers,
      selectedL1,
    );
  };

  useEffect(() => {
    const initialDataLayers = getUrlDataLayers(searchParams);
    const effectiveInitialDataLayers =
      initialDataLayers.length > 0 ? initialDataLayers : [];

    setAppliedFilters((prev) => ({
      ...prev,
      dataLayer: effectiveInitialDataLayers,
    }));
    setActiveDomainTypeTab(getDomainTypeTabFromLayers(effectiveInitialDataLayers));
    fetchparentDomainData([], effectiveInitialDataLayers, searchParams.get("l1") || "", true);
  }, []);

  // Sync navigation from URL on browser back/forward
  useEffect(() => {
    const l1 = searchParams.get("l1");
    const l2 = searchParams.get("l2");
    const l3 = searchParams.get("l3");
    const l4 = searchParams.get("l4");
    const l5 = searchParams.get("l5");
    const selectedType = getL5ViewType(searchParams.get("type"));
    const urlDataLayers = getUrlDataLayers(searchParams);
    const effectiveUrlDataLayers =
      urlDataLayers.length > 0 ? urlDataLayers : [];

    setAppliedFilters((prev) => ({
      ...prev,
      dataLayer: effectiveUrlDataLayers,
    }));
    setActiveDomainTypeTab(getDomainTypeTabFromLayers(effectiveUrlDataLayers));

    let level;
    if (l5) level = "L5";
    else if (l4) level = "L4";
    else if (l3) level = "L3";
    else if (l2) level = "L2";
    else if (l1) level = "L1";
    else return;

    // Only update if URL differs from current navigation (avoids redundant updates)
  const navMatches =
      navigation.level === level &&
      navigation.selectedL1 === l1 &&
      navigation.selectedL2 === l2 &&
      navigation.selectedL3 === l3 &&
      navigation.selectedL4 === l4 &&
      navigation.selectedL5 === l5 &&
      navigation.selectedType === (level === "L5" ? selectedType : undefined);
 
    // Keep navigation state in sync with URL, but don't skip data fetching on direct URL loads.
    if (!navMatches) {
      setNavigation({
        level,
        selectedL1: l1 || undefined,
        selectedL2: l2 || undefined,
        selectedL3: l3 || undefined,
        selectedL4: l4 || undefined,
        selectedL5: l5 || undefined,
        selectedType: level === "L5" ? selectedType : undefined,
      });
    }
 

    // Fetch data for direct URL access
    const fetchDataForLevel = async () => {
      if (level === "L1" && l1) {
        await fetchparentDomainData(
          appliedFilters.regions || [],
          effectiveUrlDataLayers,
          l1,
          false,
        );
      } else if (level === "L2" && l1 && l2) {
        await fetchDomainData(l1, l2, "", appliedFilters.regions || [], effectiveUrlDataLayers);
      } else if (level === "L3" && l1 && l2 && l3) {
        await fetchSubDomainData(l1, l2, l3, "", appliedFilters.regions || [], effectiveUrlDataLayers);
      } else if (level === "L4" && l1 && l2 && l3 && l4) {
        const details = await getProductDetails(l4);
        const productId = details?.productId ?? details?.product_id;
        await fetchProductData(l1, l2, l3, l4, productId, appliedFilters.regions || [], effectiveUrlDataLayers);
      } else if (level === "L5" && l1 && l2 && l3 && l4 && l5) {
        if (selectedType === "kpi") {
          await fetchKpiData(l1, l2, l3, l4, l5);
        } else {
          // Direct URL load for an asset requires product data to resolve the asset details.
          const details = await getProductDetails(l4);
          const productId = details?.productId ?? details?.product_id;
          await fetchProductData(
            l1,
            l2,
            l3,
            l4,
            productId,
            appliedFilters.regions || [],
            effectiveUrlDataLayers,
          );
        }
 
      }
 
    };
    fetchDataForLevel();
  }, [searchParamsKey]);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/parent-region`
        );
        setRegions(response?.data?.parent_regions || []);
      } catch (error) {
        console.error("Error fetching regions:", error);
        setRegions([]);
      }
    };
    fetchRegions().catch(console.error);
  }, []);
  const TAB_ICONS = {
    "Finance": <DollarSign size={14} />,
    "One Demand": <ShoppingBag size={14} />,
    "P&O": <Users size={14} />,
    "Product": <Database size={14} />,
    "Supply": <Truck size={14} />,
    "Test Parent Domain": <FlaskConical size={14} />,
  };
  // Create tabs from domain data
  const tabs =
    parentDomainData &&
    parentDomainData.map((domain) => ({
      id: domain.parent_L1,
      label: domain.parent_L1,
      count: domain.domains_L2.length,
      icon: TAB_ICONS[domain.parent_L1] || <Database size={18} />,
    }));

  // Get current domain
  const currentDomainSource = filteredParentDomainData || parentDomainData;
  const selectedParentFromFiltered =
    currentDomainSource &&
    currentDomainSource.find((d) => d.parent_L1 === navigation.selectedL1);
  const selectedParentFromMaster =
    parentDomainData &&
    parentDomainData.find((d) => d.parent_L1 === navigation.selectedL1);

  const currentDomain = selectedParentFromFiltered ||
    (selectedParentFromMaster
      ? {
        ...selectedParentFromMaster,
        domains_L2: [],
        total_assets: "0",
        total_products: "0",
        score: "0%",
      }
      : undefined);
  const currentL2Subdomain = domainData;
  const currentL3Subdomain = subdomainData;
  const currentL4Subdomain = productData;
  const currentL5Subdomain = currentL4Subdomain?.Assets?.find(
    (sub) => sub.data_asset_name === navigation.selectedL5
  );
  const currentL5Type =
    navigation.selectedType || getL5ViewType(searchParams.get("type"));

  // Navigation handlers
  const handleTabChange = (tabId) => {
    const newNavigation = { level: "L1", selectedL1: tabId, selectedType: undefined };
    setNavigation(newNavigation);
    setActiveDomainTypeTab("all");
    setAppliedFilters((prev) => ({
      ...prev,
      dataLayer: [],
    }));
    updateURLParams(newNavigation, []);
  };

  const handleBackToL1 = () => {
    const newNavigation = {
      level: "L1",
      selectedL1: navigation.selectedL1,
      selectedL2: undefined,
      selectedL3: undefined,
      selectedL4: undefined,
      selectedL5: undefined,
      selectedType: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleBackToL2 = () => {
    const newNavigation = {
      level: "L2",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: undefined,
      selectedL4: undefined,
      selectedL5: undefined,
      selectedType: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleBackToL3 = () => {
    const newNavigation = {
      level: "L3",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      selectedL4: undefined,
      selectedL5: undefined,
      selectedType: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleBackToL4 = () => {
    const newNavigation = {
      level: "L4",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      selectedL4: navigation.selectedL4,
      selectedL5: undefined,
      selectedType: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleBackToL5 = () => {
    const newNavigation = {
      level: "L5",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      selectedL4: navigation.selectedL4,
      selectedL5: navigation.selectedL5,
      selectedType: navigation.selectedType,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleSubdomainClick = async (subdomainName) => {
    const selectedSubdomain = currentDomain?.domains_L2.find(
      (sub) => sub.name === subdomainName
    );

    const newNavigation = {
      level: "L2",
      selectedL1: navigation.selectedL1,
      selectedL2: subdomainName,
      selectedL3: undefined,
      selectedL4: undefined,
      selectedL5: undefined,
      selectedType: undefined,
    };

    try {
      setLoading(true);
      if (selectedSubdomain?.domain_id) {
        await fetchDomainData(navigation.selectedL1, subdomainName, selectedSubdomain.domain_id, appliedFilters.regions, appliedFilters.dataLayer);
      }
      setNavigation(newNavigation);
      updateURLParams(newNavigation);
    } catch (error) {
      console.error("Error navigating to L2:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleL3Click = async (l3Name) => {
    const selectedL3 = domainData?.subdomains_L3?.find(
      (sub) => sub.name === l3Name
    );

    const newNavigation = {
      level: "L3",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: l3Name,
      selectedL4: undefined,
      selectedL5: undefined,
      selectedType: undefined,
    };

    try {
      setLoading(true);
      if (selectedL3?.subdomain_id) {
        await fetchSubDomainData(navigation.selectedL1, navigation.selectedL2, l3Name, selectedL3.subdomain_id, appliedFilters.regions, appliedFilters.dataLayer);
      }
      setNavigation(newNavigation);
      updateURLParams(newNavigation);
    } catch (error) {
      console.error("Error navigating to L3:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleL4Click = async (l4Name) => {
    const selectedProduct = currentL3Subdomain?.Products?.find(
      (sub) => sub.product_name === l4Name
    );

    const newNavigation = {
      level: "L4",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      selectedL4: l4Name,
      selectedL5: undefined,
      selectedType: undefined,
    };

    try {
      setLoading(true);
      await Promise.all([
        getProductDetails(l4Name),
        selectedProduct?.product_id
          ? fetchProductData(navigation.selectedL1, navigation.selectedL2, navigation.selectedL3, l4Name, selectedProduct.product_id, appliedFilters.regions, appliedFilters.dataLayer)
          : Promise.resolve(),
      ]);
      setNavigation(newNavigation);
      updateURLParams(newNavigation);
    } catch (error) {
      console.error("Error navigating to L4:", error);
    } finally {
      setLoading(false);
    }
  };

const handleL5Click = async (l5Name, type = "") => {
  const nextType = getL5ViewType(type);
  const newNavigation = {
    level: "L5",
    selectedL1: navigation.selectedL1,
    selectedL2: navigation.selectedL2,
    selectedL3: navigation.selectedL3,
    selectedL4: navigation.selectedL4,
    selectedL5: l5Name,
    selectedType: nextType,
  };

  setNavigation(newNavigation);
  setReportData(null);
  setReportLoading(false);
  updateURLParams(newNavigation, appliedFilters.dataLayer, type);

  if (nextType === "kpi") {
    await fetchKpiData(
      newNavigation.selectedL1,
      newNavigation.selectedL2,
      newNavigation.selectedL3,
      newNavigation.selectedL4,
      newNavigation.selectedL5,
    );
  }
};

  // Breadcrumb items
  const getBreadcrumbItems = () => {
    const items = [];

    if (navigation.selectedL1) {
      items.push({
        label: navigation.selectedL1,
        href: buildDataDomainUrl(
          { selectedL1: navigation.selectedL1 },
          { dataLayers: appliedFilters.dataLayer },
        ),
        onClick: navigation.level !== "L1" ? handleBackToL1 : undefined,
      });
    }
    if (navigation.selectedL2) {
      items.push({
        label: navigation.selectedL2,
        href: buildDataDomainUrl(
          {
            selectedL1: navigation.selectedL1,
            selectedL2: navigation.selectedL2,
          },
          { dataLayers: appliedFilters.dataLayer },
        ),
        onClick: navigation.level !== "L2" ? handleBackToL2 : undefined,
      });
    }
    if (navigation.selectedL3) {
      items.push({
        label: navigation.selectedL3,
        href: buildDataDomainUrl(
          {
            selectedL1: navigation.selectedL1,
            selectedL2: navigation.selectedL2,
            selectedL3: navigation.selectedL3,
          },
          { dataLayers: appliedFilters.dataLayer },
        ),
        onClick: navigation.level !== "L3" ? handleBackToL3 : undefined,
      });
    }
    if (navigation.selectedL4) {
      items.push({
        label: navigation.selectedL4,
        href: buildDataDomainUrl(
          {
            selectedL1: navigation.selectedL1,
            selectedL2: navigation.selectedL2,
            selectedL3: navigation.selectedL3,
            selectedL4: navigation.selectedL4,
          },
          { dataLayers: appliedFilters.dataLayer },
        ),
        onClick: navigation.level !== "L4" ? handleBackToL4 : undefined,
      });
    }
    if (navigation.selectedL5) {
      items.push({
        label: navigation.selectedL5.replace(/\|/g, "."),
        href: buildDataDomainUrl(
          {
            selectedL1: navigation.selectedL1,
            selectedL2: navigation.selectedL2,
            selectedL3: navigation.selectedL3,
            selectedL4: navigation.selectedL4,
            selectedL5: navigation.selectedL5,
          },
          { dataLayers: appliedFilters.dataLayer },
        ),
        onClick: navigation.level !== "L5" ? handleBackToL5 : undefined,
      });
    }

    return items;
  };

  if (loading) {
    return (
      <div className="relative inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." className="h-28" />
          <span className="text-gray-700 text-[20px]">
            Hang tight! The resources are loading...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#eee] relative rounded-xl">
      <div className="mx-auto">
        {/* Tab Navigation - Only show on L1 level */}
        {navigation.level === "L1" && (
          <TabNavigation
            tabs={tabs}
            activeTab={navigation.selectedL1 || "Product"}
            onTabChange={handleTabChange}
            getTabHref={(tabId) =>
              buildDataDomainUrl(
                { selectedL1: tabId },
                { dataLayers: appliedFilters.dataLayer },
              )
            }
          />
        )}

        {navigation.level === "L1" && (
          <div className="px-4 sm:px-6 pt-2 pb-0 mb-1">
  <div className="flex flex-wrap gap-0 border-b border-gray-200">
    {DOMAIN_TYPE_TABS.map((tab) => {
      const href = buildDataDomainUrl(
        { selectedL1: navigation.selectedL1 },
        { dataLayers: getLayersForDomainTypeTab(tab.id) },
      );
      const handleClick = (e) => {
        const isModifiedClick =
          e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
        if (isModifiedClick) return;
        e.preventDefault();
        handleDomainTypeTabChange(tab.id, navigation.selectedL1);
      };
      return (
     <a
          key={tab.id}
          href={href}
          onClick={handleClick}
          className={`px-4 py-2 text-sm font-medium transition-all duration-200 border-b-2 -mb-[1px] ${
            activeDomainTypeTab === tab.id
              ? "border-[#bb1ccc] text-[#bb1ccc]"
              : "border-transparent text-gray-500 hover:text-gray-800"
          }`}
        >
          {tab.label}
        </a>
      );
    })}
  </div>
</div>
        )}

        {/* Breadcrumb - Show on L2 and deeper levels */}
        {navigation.level !== "L1" && (
          <Breadcrumb items={getBreadcrumbItems()} />
        )}

        {/* Content */}
        <div
          className={`space-y-8 ${navigation.level !== "L5"
            ? "ml-[20px] mr-[20px]"
            : "bg-[#eee] pl-4 pr-5"
            }`}
        >
          {navigation.level === "L1" && domainLoading ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-3">
              <img src={heartLoading} alt="Loading..." className="h-20" />
              <span className="text-gray-700 text-base">
                Loading domain data...
              </span>
            </div>
          ) : null}

          {navigation.level === "L1" && !domainLoading && currentDomain && (
            <DomainOverview
              domain={currentDomain}
              onSubdomainClick={handleSubdomainClick}
              onFilterChange={handleFilterChange}
              appliedFilters={appliedFilters}
              navigation={navigation}
              activeDomainTypeTab={activeDomainTypeTab}
              onDomainTypeTabChange={handleDomainTypeTabChange}
              regions={regions}
            />
          )}
          {navigation.level === "L2" && currentL2Subdomain && (
            <SubdomainDetail
              subdomain={currentL2Subdomain}
              onBack={handleBackToL1}
              onL3Click={handleL3Click}
              regions={regions}
              appliedFilters={appliedFilters}
              onFilterChange={handleFilterChange}
            />
          )}
          {navigation.level === "L3" && currentL3Subdomain && (
            <ProductPage
              subdomain={currentL3Subdomain}
              onBack={handleBackToL2}
              onL4Click={handleL4Click}
              regions={regions}
              appliedFilters={appliedFilters}
              onFilterChange={handleFilterChange}
            />
          )}
          {navigation.level === "L4" && currentL4Subdomain && (
            <AssetsSummary
              subdomain={currentL4Subdomain}
              onBack={handleBackToL3}
              onL5Click={handleL5Click}
              productId={selectedProductDetails?.productId}
               userRole={role} 
            />
          )}
   
{navigation.level === "L5" && currentL5Type === "kpi" && (
<AssetsDetailsUI
    onBack={handleBackToL4}
    reportData={reportData}      
    reportLoading={reportLoading} 
  />
)}
{navigation.level === "L5" && currentL5Type === "asset" && currentL5Subdomain && (
            <AssetsDetails
              subdomain={currentL5Subdomain}
              productDetails={selectedProductDetails}
              onBack={handleBackToL4}
               userRole={role} 
            />
          )}
 
        </div>
      </div>
    </div>
  );
}

export default LandingPage;
